import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2, TrendingUp, Users, Globe, Layers, Star } from "lucide-react";
import { useSEO, orgSchema } from "@/lib/seo";
import { useServices, useIndustries, useSettings } from "@/lib/content";
import { ENGAGEMENT_MODELS } from "@/data/engagement";
import { SectionHeading } from "@/components/common/SectionHeading";
import { StarMotif } from "@/components/common/StarMotif";
import { CTABlock } from "@/components/common/CTABlock";
import { absUrl } from "@/components/admin/UploadWidget";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";



const ICON_MAP = { TrendingUp, Users, Globe, Layers };

const DEFAULT_METRICS = [
    { value: "10+", label: "Years delivering to global enterprises", icon: "TrendingUp" },
    { value: "500+", label: "Engineers across product, cloud, data & AI", icon: "Users" },
    { value: "6", label: "Global timezones actively served", icon: "Globe" },
    { value: "40+", label: "Fortune 1000 & scaling-startup partners", icon: "Layers" },
];

export default function Home() {
    const { items: SERVICES } = useServices();
    const { items: INDUSTRIES } = useIndustries();
    const { data: settings } = useSettings("home");
    const hero = settings?.hero || {};
    const metrics = settings?.metrics || DEFAULT_METRICS;
    const svcSec = settings?.services_section || {};
    const indSec = settings?.industries_section || {};
    const stSec = settings?.stories_section || {};
    const ptSec = settings?.prototypes_section || {};
    const engSec = settings?.engagement_section || {};
    const blogSec = settings?.blog_section || {};
    const ctaSec = settings?.cta_block || {};
    useSEO({
        title: "RKS Systems — Strategic Offshore Engineering Partner for Global Enterprises",
        description:
            "RKS Systems delivers product engineering, cloud, data, AI, quality, automation and security outcomes to US and global enterprises. Senior engineers. Transparent governance. Real production results.",
        canonical: typeof window !== "undefined" ? window.location.href : undefined,
        jsonLd: orgSchema(),
    });

    const [featured, setFeatured] = useState({ products: [], stories: [], blogs: [] });
    useEffect(() => {
        (async () => {
            try {
                const [p, s, b] = await Promise.all([
                    api.get("/products"),
                    api.get("/success-stories"),
                    api.get("/blogs"),
                ]);
                setFeatured({
                    products: (p.data.items || []).slice(0, 3),
                    stories: (s.data.items || []).slice(0, 2),
                    blogs: (b.data.items || []).slice(0, 3),
                });
            } catch { /* ignore */ }
        })();
    }, []);

    return (
        <div data-testid="page-home">
            {/* HERO */}
            <section className="relative overflow-hidden bg-[hsl(222_47%_7%)] text-white noise" data-testid="home-hero">
                <div className="absolute inset-0 grid-lines opacity-40" />
                <div className="absolute -right-24 top-8 h-[520px] w-[520px] opacity-[0.09]">
                    <StarMotif className="h-full w-full text-primary" label="RKS" labelColor="currentColor" />
                </div>

                <div className="container-app relative pt-16 pb-28 lg:pt-20 lg:pb-40">
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-7 anim-reveal">
                            <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary tracking-wide">
                                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                                {hero.badge || "Cost-effective Offshore Technology Partner"}
                            </div>
                            <h1 className="mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
                                {hero.headline_pre || "Engineering "}<span className="text-primary">{hero.headline_highlight || "outcomes"}</span>{hero.headline_post ? <><br className="hidden sm:block" /><span className="italic font-semibold text-white/85">{hero.headline_post.replace(/^,\s*/, "")}</span></> : <><br className="hidden sm:block" /><span className="italic font-semibold text-white/85">not deliverables.</span></>}
                            </h1>
                            <p className="mt-7 max-w-2xl text-lg text-white/70 leading-relaxed">
                                {hero.subheadline || "We build, modernize and operate the platforms behind the world's most demanding enterprises — with senior engineers, transparent governance, and outcomes that hold up in production."}
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <Link
                                    to={hero.primary_cta_link || "/schedule-meeting"}
                                    data-testid="hero-schedule-btn"
                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3.5 font-semibold text-white btn-lift hover:bg-primary/90 shadow-lg shadow-primary/30"
                                >
                                    {hero.primary_cta_label || "Schedule a Meeting"}
                                    <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                                </Link>
                                <Link
                                    to={hero.secondary_cta_link || "/what-we-do"}
                                    data-testid="hero-services-btn"
                                    className="inline-flex items-center justify-center gap-2 rounded-md border border-white/20 bg-white/[0.04] px-6 py-3.5 font-semibold text-white btn-lift hover:bg-white/[0.08] hover:border-white/40"
                                >
                                    {hero.secondary_cta_label || "Explore Services"}
                                </Link>
                            </div>

                            <div className="mt-12 flex items-center gap-5 flex-wrap">
                                <div className="flex items-center gap-1.5 text-sm text-white/70">
                                    <div className="flex">
                                        {[0,1,2,3,4].map((i) => (
                                            <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                                        ))}
                                    </div>
                                    <span className="ml-2 text-white/80 font-semibold">{hero.trust_stars_rating || "4.9/5"}</span>
                                    <span>{hero.trust_stars_label || "average client satisfaction"}</span>
                                </div>
                                <div className="h-4 w-px bg-white/15" />
                                <div className="text-sm text-white/70">
                                    <span className="text-white font-semibold">{hero.trust_availability || "99.995%"}</span> {hero.trust_availability_label || "platform availability sustained"}
                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-5 anim-reveal anim-reveal-delay-2">
                            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10">
                                <img
                                    src={hero.image ? (hero.image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${hero.image}` : hero.image) : "https://images.unsplash.com/photo-1522071820081-009f0129c71c"}
                                    alt="Senior engineers collaborating"
                                    className="h-full w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(222_47%_7%)] via-transparent to-transparent" />
                                <div className="absolute bottom-6 left-6 right-6 rounded-xl bg-white/95 backdrop-blur p-5 text-foreground border border-black/5">
                                    <div className="flex items-center gap-3">
                                        <StarMotif className="h-8 w-8 text-primary" label="RKS" labelColor="#ffffff" />
                                        <div>
                                            <div className="text-xs text-muted-foreground uppercase tracking-widest font-semibold">Trusted by</div>
                                            <div className="font-heading font-bold text-foreground">{hero.trusted_by_count || "40+ enterprises worldwide"}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* METRICS STRIP */}
            <section className="border-y border-border bg-white" data-testid="home-metrics">
                <div className="container-app py-10 lg:py-14 grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
                    {metrics.map((m) => {
                        const Icon = ICON_MAP[m.icon] || TrendingUp;
                        return (
                            <div key={m.label} className="flex items-start gap-4">
                                <div className="h-10 w-10 rounded-md bg-secondary text-primary flex items-center justify-center flex-shrink-0">
                                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="font-heading text-3xl lg:text-4xl font-extrabold text-foreground leading-none">{m.value}</div>
                                    <div className="mt-1.5 text-sm text-muted-foreground leading-snug">{m.label}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-20 lg:py-28" data-testid="home-services">
                <div className="container-app">
                    <SectionHeading
                        eyebrow={svcSec.eyebrow}
                        title={svcSec.title || "Seven practice areas. One accountability line."}
                        description={svcSec.description || "Senior-first engineering pods across the modern stack — with transparent governance and outcome-based delivery."}
                    />
                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {SERVICES.map((s, idx) => (
                            <Link
                                key={s.slug}
                                to={`/what-we-do/${s.slug}`}
                                data-testid={`home-service-card-${s.slug}`}
                                className={`group relative overflow-hidden rounded-xl border border-border bg-card p-8 transition-all duration-300 hover:border-primary/40 hover:shadow-lg hover:-translate-y-1 ${idx === 0 ? "md:col-span-2 md:row-span-1 md:min-h-[320px]" : ""}`}
                            >
                                <div className="absolute -right-8 -top-8 h-24 w-24 opacity-[0.06] group-hover:opacity-[0.14] transition-opacity">
                                    <StarMotif className="h-full w-full text-primary" label="RKS" labelColor="currentColor" />
                                </div>
                                <div className="relative">
                                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-secondary text-primary">
                                        <StarMotif className="h-5 w-5" />
                                    </div>
                                    <h3 className="mt-5 font-heading text-xl font-bold text-foreground">{s.title}</h3>
                                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.tagline}</p>
                                    <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                                        Explore <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* INDUSTRIES */}
            <section className="py-20 lg:py-28 bg-secondary/40" data-testid="home-industries">
                <div className="container-app">
                    <SectionHeading
                        eyebrow={indSec.eyebrow}
                        title={indSec.title || "Deep patterns in eight industries."}
                        description={indSec.description || "Reference architectures, industry playbooks, and delivery leaders who have shipped in your domain."}
                    />
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {INDUSTRIES.map((i) => (
                            <Link
                                key={i.slug}
                                to={`/solutions/${i.slug}`}
                                data-testid={`home-industry-${i.slug}`}
                                className="group relative overflow-hidden rounded-xl aspect-[4/5] border border-border bg-white transition-all duration-300 hover:border-primary/40 hover:shadow-lg"
                            >
                                <img src={absUrl(i.image)} alt={i.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(222_47%_7%)]/95 via-[hsl(222_47%_7%)]/40 to-transparent" />
                                <div className="absolute inset-x-0 bottom-0 p-5">
                                    <div className="font-heading font-bold text-white text-lg">{i.name}</div>
                                    <div className="mt-1 text-xs text-white/70 line-clamp-2">{i.headline}</div>
                                    <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                                        Explore <ArrowUpRight className="h-3 w-3" strokeWidth={2} />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURED SUCCESS STORIES */}
            <section className="py-20 lg:py-28" data-testid="home-stories">
                <div className="container-app">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <SectionHeading eyebrow={stSec.eyebrow} title={stSec.title || "Outcomes clients can point to."} />
                        <Link to="/success-stories" data-testid="home-stories-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                            All success stories <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </Link>
                    </div>
                    <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {featured.stories.map((s) => (
                            <Link
                                key={s.id}
                                to={`/success-stories/${s.id}`}
                                data-testid={`home-story-${s.id}`}
                                className="group relative overflow-hidden rounded-xl border border-border bg-card transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                            >
                                <div className="aspect-[16/9] overflow-hidden bg-secondary">
                                    <img src={absUrl(s.cover_image)} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                </div>
                                <div className="p-7">
                                    <span className="overline">{s.industry}</span>
                                    <h3 className="mt-3 font-heading text-xl font-bold text-foreground leading-snug">{s.title}</h3>
                                    <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{s.challenge}</p>
                                    <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                                        Read case study <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURED PRODUCTS */}
            <section className="py-20 lg:py-28 bg-[hsl(222_47%_7%)] text-white noise" data-testid="home-products">
                <div className="container-app">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div className="max-w-2xl">
                            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
                                {ptSec.title_pre || "Working, opinionated "}<span className="text-primary">{ptSec.title_highlight || "products"}</span>{ptSec.title_post || "."}
                            </h2>
                            <p className="mt-4 text-white/70 max-w-xl leading-relaxed">
                                {ptSec.description || "Explore products we've built for real business problems — with the tech stack, the constraint set, and the outcome."}
                            </p>
                        </div>
                        <Link to="/products" data-testid="home-products-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                            View all products <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </Link>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {featured.products.map((p) => (
                            <Link
                                key={p.id}
                                to={`/products/${p.id}`}
                                data-testid={`home-product-${p.id}`}
                                className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] transition-all duration-300 hover:border-primary/50 hover:-translate-y-1"
                            >
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img src={absUrl(p.thumbnail)} alt={p.title} className="h-full w-full object-cover opacity-90 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100" />
                                </div>
                                <div className="p-6">
                                    <div className="text-xs font-semibold text-primary tracking-wider uppercase">{p.category}</div>
                                    <h3 className="mt-2 font-heading font-bold text-white leading-tight">{p.title}</h3>
                                    <p className="mt-2 text-sm text-white/60 line-clamp-2">{p.description}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* ENGAGEMENT MODELS */}
            <section className="py-20 lg:py-28" data-testid="home-engagement">
                <div className="container-app">
                    <SectionHeading
                        eyebrow={engSec.eyebrow}
                        title={engSec.title || "Two ways to work with us."}
                        description={engSec.description || "Pick the model that matches your delivery reality — with the same governance and senior engineering on either side."}
                    />
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                        {ENGAGEMENT_MODELS.map((e) => (
                            <div key={e.slug} data-testid={`home-engagement-${e.slug}`} className="relative overflow-hidden rounded-xl border border-border bg-card p-8 lg:p-10">
                                <div className="absolute -right-6 -top-6 opacity-[0.05]">
                                    <StarMotif className="h-32 w-32 text-primary" label="RKS" labelColor="#ffffff" />
                                </div>
                                <span className="overline">{e.name}</span>
                                <h3 className="mt-3 font-heading text-2xl font-bold">{e.tagline}</h3>
                                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{e.overview}</p>
                                <ul className="mt-6 space-y-2.5">
                                    {e.benefits.slice(0, 4).map((b) => (
                                        <li key={b} className="flex items-start gap-2.5 text-sm">
                                            <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" strokeWidth={1.75} />
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link to="/engagement-models" data-testid={`home-engagement-cta-${e.slug}`} className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                                    Learn more <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* LATEST BLOGS */}
            <section className="py-20 lg:py-28 bg-secondary/40" data-testid="home-blog">
                <div className="container-app">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <SectionHeading eyebrow={blogSec.eyebrow} title={blogSec.title || "Notes from the field."} />
                        <Link to="/blog" data-testid="home-blog-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                            All articles <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </Link>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {featured.blogs.map((b) => (
                            <Link
                                key={b.id}
                                to={`/blog/${b.id}`}
                                data-testid={`home-blog-${b.id}`}
                                className="group overflow-hidden rounded-xl border border-border bg-card transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                            >
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img src={absUrl(b.cover_image)} alt={b.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                </div>
                                <div className="p-6">
                                    <div className="text-xs font-semibold text-primary tracking-wider uppercase">{b.category}</div>
                                    <h3 className="mt-2 font-heading font-bold text-foreground leading-tight">{b.title}</h3>
                                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{b.excerpt}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <CTABlock
                title={ctaSec.title}
                description={ctaSec.description}
                primaryLabel={ctaSec.primary_cta_label}
                primaryTo={ctaSec.primary_cta_link}
                secondaryLabel={ctaSec.secondary_cta_label}
                secondaryTo={ctaSec.secondary_cta_link}
            />
        </div>
    );
}
